/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, Fragment, useCallback, useRef } from "react";
import {
  Check,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Search,
  RotateCcw,
} from "lucide-react";
import { ListItem } from "@mui/material";
import alertStyles from "../../../Samarth Module/pages/Alerts/alert.module.css";
import resetBtn from "../../../assets/images/resetBtn.png";

import Filters from "../../../assets/images/filters.svg";

import "./tntLinkage.css";
import "../emailTriaging/emailTriaging.css";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import ColumnSortIcon from "Agentic Module/components/common/columnSortIcon";
import {
  emailTriageExportData,
  emailTriageGetUserPrefs,
  emailTriageSaveUserPrefs,
} from "../emailTriaging/emailTriageApi";
import MoreFilters from "../emailTriaging/MoreFilters";
import { useRegisterAiChatScreen } from "../../utils/aiChatScreenContext";
import {
  buildTntActionItem,
  MASTER_DATA_PAGE_SIZE,
  MASTER_FIELD_TO_LOOKUP_TYPE,
  normalizeMasterDataResponse,
  tntLinkageGetFilters,
  tntLinkageGetMasterData,
  tntLinkageGetTntData,
  tntLinkageSaveActions,
  UNSAVED_CHANGES_DISCARD_MESSAGE,
  UNSAVED_CHANGES_DISCARD_TITLE,
} from "./tntLinkageApi";
import { TNT_LINKAGE_SELECTED_FILTERS_STORAGE_KEY, buildDefaultTntSelectedFilters, buildEmptyTntFilterOptionsFromUserPrefs } from "./tntLinkageConstants";
import { buildCorrectionOptionsFromFilters, buildRowOverrideFromSelections, buildRmpmFiltersFromUserPrefs, buildTntUserPrefsPayload, resolveMaterialDescForCodeAsync, resolveSiteClusterForCodeAsync, formatTntFilterOptionsForDisplay, generateTntFilterLabel, mergeTntSelectedFiltersWithUserPrefs, normalizePersistedTntSelectedFilters, resolveTntPrefFilterNameToKey, TNT_CONFIDENCE_VALUE_LABELS, TNT_LINKAGE_STATUS_VALUE_LABELS, TNT_USER_PREFS_SCREEN_ID } from "./tntLinkageUtils";
import {
  getReviewerIdFromStorage,
  getUserEmailFromStorage,
  loadPersistedSelectedFilters,
  savePersistedSelectedFilters,
  clearPersistedSelectedFilters,
} from "../emailTriaging/emailTriageUtils";
import MultiSelectWithSearchInput from "../../components/infiniteMultiSelect/withInfiniteMultiSelect";
import { formatDisplayDdMmYy } from "../emailTriaging/emailTriaging";
import TripTrackingModal from "./tripTrackingModal";
import { TntLinkageTableSkeletonRows, PaginationSkeleton, FilterBarSkeleton } from "../../components/skeletonLoader/AgenticPageSkeleton";
import PropTypes from "prop-types";

const DEFAULT_PAGE_SIZE = 20;
/** Max filters shown in the toolbar; others are available via More Filters. */
const VISIBLE_FILTER_LIMIT = 9;

function isConfidenceFilterKey(filterKey) {
  return resolveTntPrefFilterNameToKey(filterKey) === "confidence";
}

function isLinkageStatusFilterKey(filterKey) {
  return resolveTntPrefFilterNameToKey(filterKey) === "linkage_status";
}

function resolveToolbarFilterKey(filter, filterOptions) {
  if (filter?.filterKey) return filter.filterKey;
  const fromPref = resolveTntPrefFilterNameToKey(filter?.prefName);
  if (fromPref) return fromPref;
  return Object.keys(filterOptions).find((k) => generateTntFilterLabel(k, filter?.prefName) === filter?.label);
}

/** Sub-header columns; `sortKey` is the API `sort=` field when it differs from the row key. */
const TNT_TABLE_COLUMNS = [
  { key: "tripId", label: "Trip ID", sortable: true, headerClass: "tnt-td-divider" },
  { key: "destination", label: "Destination", sortable: true },
  { key: "tntVendorName", sortKey: "vendorName", label: "Vendor", sortable: true },
  { key: "tntMaterialDesc", sortKey: "materialDesc", label: "Material", sortable: true },
  { key: "lastLocation", label: "Last Location", sortable: true },
  { key: "distanceRemaining", label: "Distance Remaining", sortable: true },
  { key: "dispatchDate", label: "Dispatch Date", sortable: true, headerClass: "tnt-td-divider" },
  { key: "siteCode", label: "Site Code | Cluster", sortable: true },
  { key: "vendorCode", label: "Vendor Code | Name", sortable: true },
  { key: "materialCode", label: "Material Code | Desc", sortable: true, headerClass: "tnt-td-divider" },
  { key: "confidence", sortKey: "confidenceScorePct", label: "Confidence Score", sortable: true, center: true },
  { key: "_action", label: "Action", sortable: false, center: true, headerClass: "tnt-td-divider" },
  { key: "poDispatchDate", label: "Dispatch Date", sortable: true },
  { key: "poNumber", label: "PO Number", sortable: true },
];

const DISPLAY_VALUE_TO_API_VALUE = {
  confidence: Object.fromEntries(
    Object.entries(TNT_CONFIDENCE_VALUE_LABELS).map(([apiValue, label]) => [
      label,
      apiValue,
    ]),
  ),
  linkage_status: Object.fromEntries(
    Object.entries(TNT_LINKAGE_STATUS_VALUE_LABELS).map(([apiValue, label]) => [
      label,
      apiValue,
    ]),
  ),
};

function toDisplayFilterValues(filterKey, values) {
  const list = Array.isArray(values) ? values : [];
  if (isConfidenceFilterKey(filterKey)) {
    return list.map(
      (value) => TNT_CONFIDENCE_VALUE_LABELS[String(value ?? "").trim()] ?? String(value ?? "").trim(),
    );
  }
  if (isLinkageStatusFilterKey(filterKey)) {
    return list.map(
      (value) => TNT_LINKAGE_STATUS_VALUE_LABELS[String(value ?? "").trim()] ?? String(value ?? "").trim(),
    );
  }
  return list;
}

function toApiFilterValues(filterKey, values) {
  const list = Array.isArray(values) ? values : [];
  const normalizedKey = resolveTntPrefFilterNameToKey(filterKey);
  const reverseMap = DISPLAY_VALUE_TO_API_VALUE[normalizedKey];
  if (!reverseMap) return list;
  return list.map((value) => reverseMap[String(value ?? "").trim()] ?? String(value ?? "").trim());
}

function hasCorrectionDropdownSelection(selections) {
  if (!selections || typeof selections !== "object") return false;
  return Boolean(
    String(selections.siteCode ?? "").trim() ||
    String(selections.vendorCode ?? "").trim() ||
    String(selections.materialCode ?? "").trim(),
  );
}

function normalizeMasterSelection(value) {
  if (value == null) return { code: "", name: "" };
  if (typeof value === "object") {
    return {
      code: String(value.code ?? "").trim(),
      name: String(value.name ?? "").trim(),
    };
  }
  return { code: String(value).trim(), name: "" };
}

/** Other correction dropdown picks to cascade into `/get_master_date` query params. */
function buildCorrectionCascadeFilters(fieldType, selections) {
  const sel = selections && typeof selections === "object" ? selections : {};
  const site = String(sel.siteCode ?? "").trim();
  const vendor = String(sel.vendorCode ?? "").trim();
  const material = String(sel.materialCode ?? "").trim();
  const filters = {};

  if (fieldType !== "siteCode" && site) filters.site_code = site;
  if (fieldType !== "vendorCode" && vendor) filters.vendor_code = vendor;
  if (fieldType !== "materialCode" && material) filters.material_code = material;

  return filters;
}

/** Returns `text-emerald-600` / `text-amber-600` / `text-red-600`-equivalent classes. */
function confidenceClass(value) {
  if (value >= 70) return "tnt-confidence tnt-confidence--high";
  if (value >= 50) return "tnt-confidence tnt-confidence--medium";
  return "tnt-confidence tnt-confidence--low";
}

/** Searchable single-select backed by `/api/v1/get_master_date`. */
export function SearchableCorrectionSelect({
  value,
  onChange,
  placeholder,
  fieldType,
  contextSelections = {},
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const requestIdRef = useRef(0);
  const lookupType = MASTER_FIELD_TO_LOOKUP_TYPE[fieldType];

  const cascadeFilters = useMemo(
    () => buildCorrectionCascadeFilters(fieldType, contextSelections),
    [fieldType, contextSelections],
  );

  const displayOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    options.forEach((o) => {
      const code = String(o?.code ?? "").trim();
      if (!code || seen.has(code)) return;
      seen.add(code);
      list.push({
        code,
        name: String(o?.name ?? code).trim() || code,
      });
    });
    if (value) {
      const v = String(value).trim();
      if (v && !seen.has(v)) {
        list.push({ code: v, name: v });
      }
    }
    return list.sort((a, b) => a.code.localeCompare(b.code));
  }, [options, value]);

  const selectedOption = useMemo(
    () => displayOptions.find((o) => o.code === String(value ?? "").trim()) ?? null,
    [displayOptions, value],
  );

  const mergeOptions = useCallback((prev, nextItems) => {
    const seen = new Set(prev.map((o) => o.code));
    const merged = [...prev];
    nextItems.forEach((item) => {
      if (!item?.code || seen.has(item.code)) return;
      seen.add(item.code);
      merged.push(item);
    });
    return merged;
  }, []);

  const fetchMasterOptions = useCallback(
    async ({ searchTerm, pageNum, append }) => {
      const reqId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const data = await tntLinkageGetMasterData({
          search: searchTerm,
          lookupType,
          page: pageNum,
          pageSize: MASTER_DATA_PAGE_SIZE,
          ...cascadeFilters,
        });
        if (reqId !== requestIdRef.current) return;
        const normalized = normalizeMasterDataResponse(data, fieldType, {
          page: pageNum,
          pageSize: MASTER_DATA_PAGE_SIZE,
        });
        setPage(normalized.page);
        setHasMore(normalized.hasMore);
        setOptions((prev) =>
          append ? mergeOptions(prev, normalized.items) : normalized.items,
        );
      } catch (error) {
        console.error("Failed to fetch master data:", error);
        if (reqId === requestIdRef.current) {
          if (!append) setOptions([]);
          setHasMore(false);
        }
      } finally {
        if (reqId === requestIdRef.current) {
          if (append) setLoadingMore(false);
          else setLoading(false);
        }
      }
    },
    [fieldType, lookupType, mergeOptions, cascadeFilters],
  );

  useEffect(() => {
    if (!open) return undefined;
    setPage(1);
    setHasMore(false);
    setOptions([]);
    const timer = setTimeout(() => {
      fetchMasterOptions({ searchTerm: search, pageNum: 1, append: false });
    }, search.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [open, search, fetchMasterOptions, cascadeFilters]);

  const handleListScroll = useCallback(
    (event) => {
      const list = event.currentTarget;
      if (loading || loadingMore || !hasMore) return;
      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 8) {
        fetchMasterOptions({
          searchTerm: search,
          pageNum: page + 1,
          append: true,
        });
      }
    },
    [loading, loadingMore, hasMore, search, page, fetchMasterOptions],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const displayValue = value ? String(value) : "";
  const hasValue = Boolean(displayValue);
  const triggerPrimary = selectedOption?.code || displayValue;
  const triggerSecondary =
    selectedOption && selectedOption.name !== selectedOption.code
      ? selectedOption.name
      : "";

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange({ code: "", name: "" });
    setOpen(false);
    setSearch("");
  };

  function renderSearchOption(
    opt,
    displayValue,
    onChange,
    setOpen,
  ) {
    const isSelected = displayValue === opt.code;

    const handleClick = () => {
      onChange({
        code: opt.code,
        name: opt.name,
      });

      setOpen(false);
    };

    return (
      <li key={opt.code}>
        <button
          type="button"
          aria-pressed={isSelected}
          className={`tnt-search-select-option${isSelected
            ? " tnt-search-select-option--active"
            : ""
            }`}
          onClick={handleClick}
        >
          <span className="tnt-search-select-option-inner">
            <span className="tnt-search-select-option-code">
              {opt.code}
            </span>

            <span
              className="tnt-search-select-option-name"
              title={opt.name}
            >
              {opt.name}
            </span>
          </span>
        </button>
      </li>
    );
  }

  function renderOptionListContent({
    loading,
    displayOptions,
    displayValue,
    onChange,
    setOpen,
  }) {
    if (loading && displayOptions.length === 0) {
      return (
        <li className="tnt-search-select-empty">
          Loading…
        </li>
      );
    }

    if (displayOptions.length === 0) {
      return (
        <li className="tnt-search-select-empty">
          No matches
        </li>
      );
    }

    return displayOptions.map((opt) =>
      renderSearchOption(
        opt,
        displayValue,
        onChange,
        setOpen,
      ),
    );
  }

  return (
    <div className="tnt-search-select" ref={rootRef}>
      <div className="tnt-search-select-trigger-wrap">
        <button
          type="button"
          className="tnt-search-select-trigger"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span
            className={
              hasValue
                ? "tnt-search-select-value tnt-search-select-value--stacked"
                : "tnt-search-select-placeholder"
            }
          >
            {hasValue ? (
              <>
                <span className="tnt-search-select-value-code">{triggerPrimary}</span>
                {triggerSecondary ? (
                  <span className="tnt-search-select-value-name" title={triggerSecondary}>
                    {triggerSecondary}
                  </span>
                ) : null}
              </>
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown size={12} className="tnt-search-select-chevron" aria-hidden />
        </button>
        {hasValue ? (
          <button
            type="button"
            className="tnt-search-select-clear"
            onClick={handleClear}
            aria-label={`Clear ${placeholder}`}
            title="Clear selection"
          >
            <X size={12} aria-hidden />
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="tnt-search-select-panel">
          <div className="tnt-search-select-search-wrap">
            <Search size={12} className="tnt-search-select-search-icon" aria-hidden />
            <input
              type="text"
              className="tnt-search-select-search"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={`${placeholder} search`}
            />
          </div>
          <ul
            className="tnt-search-select-list"
            ref={listRef}
            onScroll={handleListScroll}
          >
            {hasValue ? (
              <li role="none">
                <button
                  type="button"
                  className="tnt-search-select-option tnt-search-select-option--clear"
                  onClick={handleClear}
                >
                  Clear selection
                </button>
              </li>
            ) : null}
            {renderOptionListContent({
              loading,
              displayOptions,
              displayValue,
              onChange,
              setOpen,
            })}
            {loadingMore && (
              <li className="tnt-search-select-empty">
                Loading more…
              </li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

SearchableCorrectionSelect.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  fieldType: PropTypes.string.isRequired,
  contextSelections: PropTypes.object,
};

function TripIdCell({ tripId, onOpen }) {
  if (!tripId) {
    return <div className="tnt-trip-id">—</div>;
  }
  return (
    <button
      type="button"
      className="tnt-trip-id tnt-trip-id--btn"
      onClick={() => onOpen?.(tripId)}
      title="View trip tracking"
    >
      {tripId}
    </button>
  );
}

TripIdCell.propTypes = {
  tripId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onOpen: PropTypes.func,
};

function CodeNamePair({ code, name }) {
  const sub = name != null && String(name).trim() !== "" ? String(name).trim() : "—";
  return (
    <>
      <div className="tnt-code">{code || ""}</div>
      <div className="tnt-sub">{sub}</div>
    </>
  );
}

function MaterialCell({ code, desc }) {
  const sub =
    desc != null && String(desc).trim() !== "" ? String(desc).trim() : "—";
  return (
    <>
      <div className="tnt-strong">{code || ""}</div>
      <div className="tnt-sub tnt-sub--mono">{sub}</div>
    </>
  );
}

CodeNamePair.propTypes = {
  code: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

MaterialCell.propTypes = {
  code: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
};

function formatDistanceRemainingKm(value) {
  const text = value != null ? String(value).trim() : "";
  if (!text) return "—";
  return /\bkm\b/i.test(text) ? text : `${text} Km`;
}

function MatchStatusPill({ state }) {
  if (state === "matched") {
    return (
      <span className="tnt-match-pill">
        <Check size={12} className="tnt-icon-green" aria-hidden /> Matched
      </span>
    );
  }
  if (state === "rejected") {
    return (
      <span className="tnt-match-pill tnt-match-pill--rejected">
        <X size={12} className="tnt-icon-red" aria-hidden /> Rejected
      </span>
    );
  }
  return (
    <span className="tnt-match-pill tnt-match-pill--validated">
      <Check size={12} className="tnt-icon-validated" aria-hidden /> User Validated
    </span>
  );
}

MatchStatusPill.propTypes = {
  state: PropTypes.string.isRequired,
};

function ActionButtons({ onAccept, onReject }) {
  return (
    <div className="tnt-action-btns">
      <button
        type="button"
        onClick={onAccept}
        className="tnt-icon-btn tnt-icon-btn--accept"
        title="Accept match"
        aria-label="Accept match"
      >
        <Check size={14} />
      </button>
      <button
        type="button"
        onClick={onReject}
        className="tnt-icon-btn tnt-icon-btn--reject"
        title="Reject match"
        aria-label="Reject match"
      >
        <X size={14} />
      </button>
    </div>
  );
}

ActionButtons.propTypes = {
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

function CorrectionRow({
  rowId,
  selections,
  onSelect,
  onConfirm,
  onCancel,
}) {
  const sel = selections[rowId] || {};
  const hasSelection = hasCorrectionDropdownSelection(sel);
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState(false);

  const handleConfirmClick = () => {
    if (!hasSelection) {
      const trimmed = remark.trim();
      if (!trimmed) {
        setRemarkError(true);
        return;
      }
      onConfirm(rowId, trimmed);
      return;
    }
    setRemarkError(false);
    onConfirm(rowId, "");
  };

  return (
    <tr className="tnt-correction-row">
      <td className="tnt-td tnt-td-divider" />
      <td className="tnt-td" />
      <td className="tnt-td" />
      <td className="tnt-td" />
      <td className="tnt-td" />
      <td className="tnt-td" />
      <td className="tnt-td tnt-td-divider" />

      <td className="tnt-td tnt-correction-cell">
        <SearchableCorrectionSelect
          value={sel.siteCode || ""}
          onChange={(v) => onSelect(rowId, "siteCode", v)}
          placeholder="Select Site"
          fieldType="siteCode"
          contextSelections={sel}
        />
      </td>
      <td className="tnt-td tnt-correction-cell">
        <SearchableCorrectionSelect
          value={sel.vendorCode || ""}
          onChange={(v) => onSelect(rowId, "vendorCode", v)}
          placeholder="Select Vendor"
          fieldType="vendorCode"
          contextSelections={sel}
        />
      </td>
      <td className="tnt-td tnt-correction-cell tnt-td-divider">
        <SearchableCorrectionSelect
          value={sel.materialCode || ""}
          onChange={(v) => onSelect(rowId, "materialCode", v)}
          placeholder="Select Material"
          fieldType="materialCode"
          contextSelections={sel}
        />
      </td>

      <td className="tnt-td" />
      <td className="tnt-td tnt-td-divider tnt-correction-actions-cell">

        <div className="tnt-correction-remark-wrap">
          <label className="tnt-correction-remark-label" htmlFor={`tnt-remark-${rowId}`}>
            Remark <span className="tnt-correction-remark-required">*</span>
          </label>
          <input
            id={`tnt-remark-${rowId}`}
            type="text"
            className={`tnt-correction-remark-input${remarkError && !hasSelection ? " tnt-correction-remark-input--error" : ""}`}
            placeholder="Enter remark (required)"
            value={remark}
            onChange={(e) => {
              setRemark(e.target.value);
              if (remarkError && e.target.value.trim()) setRemarkError(false);
            }}
            aria-invalid={remarkError}
            aria-describedby={remarkError && !hasSelection ? `tnt-remark-error-${rowId}` : undefined}
          />
          {remarkError && !hasSelection ? (
            <p id={`tnt-remark-error-${rowId}`} className="tnt-correction-remark-error" role="alert">
              Remark is required when no site, vendor, or material code is selected.
            </p>
          ) : null}
        </div>

        <div className="tnt-correction-actions">
          <button
            type="button"
            onClick={handleConfirmClick}
            className="tnt-btn tnt-btn-primary"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="tnt-btn tnt-btn-secondary"
          >
            Cancel
          </button>
        </div>
      </td>
      <td className="tnt-td" />
      <td className="tnt-td" />
    </tr>
  );
}

CorrectionRow.propTypes = {
  rowId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  selections: PropTypes.objectOf(
    PropTypes.shape({
      siteCode: PropTypes.string,
      vendorCode: PropTypes.string,
      materialCode: PropTypes.string,
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

function getCorrectionPreview({
  isShowingDropdowns,
  rowId,
  dropdownSelections,
  correctionOptions,
  tableData,
  filterOptions,
}) {
  if (!isShowingDropdowns) {
    return {};
  }

  return buildRowOverrideFromSelections(
    dropdownSelections[rowId] || {},
    correctionOptions,
    tableData,
    filterOptions,
  );
}

function hasSavedManualSelection(row) {
  return Boolean(
    row.manualSelectedSiteCode ||
    row.manualSelectedVendorCode ||
    row.manualSelectedMaterialCode,
  );
}

function shouldShowAgentOverride(rowState, row, correctionPreview) {
  return (
    rowState === "user-validated" ||
    Object.keys(correctionPreview).length > 0 ||
    hasSavedManualSelection(row)
  );
}

function buildDisplayRow(row, rowOverride, correctionPreview) {
  return {
    ...row,
    ...rowOverride,
    ...correctionPreview,
  };
}

function getOverriddenCellClass(baseClass, showAgentOverride) {
  return `${baseClass}${showAgentOverride ? " tnt-td--overridden" : ""}`;
}

function TntActionCell({
  rowId,
  rowState,
  pendingAction,
  onResetRowAction,
  onAction,
}) {
  if (!rowState) {
    return (
      <td className="tnt-td tnt-td-center tnt-td-divider">
        <ActionButtons
          onAccept={() => onAction(rowId, "matched")}
          onReject={() => onAction(rowId, "not-matched")}
        />
      </td>
    );
  }

  return (
    <td className="tnt-td tnt-td-center tnt-td-divider">
      <div className="tnt-status-with-reset">
        <MatchStatusPill state={rowState} />

        {pendingAction ? (
          <button
            type="button"
            className="tnt-icon-btn tnt-icon-btn--reset"
            title="Reset action"
            aria-label="Reset action"
            onClick={() => onResetRowAction(rowId)}
          >
            <RotateCcw size={14} aria-hidden />
          </button>
        ) : null}
      </div>
    </td>
  );
}

TntActionCell.propTypes = {
  rowId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  rowState: PropTypes.string,
  pendingAction: PropTypes.bool,
  onResetRowAction: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
};

function TntMainRow({
  rowId,
  rowState,
  displayRow,
  showAgentOverride,
  pendingAction,
  onOpenTrip,
  onResetRowAction,
  onAction,
}) {
  const agentCellClass = getOverriddenCellClass(
    "tnt-td",
    showAgentOverride,
  );

  const agentMaterialCellClass = getOverriddenCellClass(
    "tnt-td tnt-td-divider",
    showAgentOverride,
  );

  return (
    <tr className="tnt-row">
      <td className="tnt-td tnt-td-divider">
        <TripIdCell
          tripId={displayRow.tripId}
          onOpen={(tripId) => onOpenTrip(tripId, displayRow)}
        />
      </td>

      <td className="tnt-td">
        <div className="tnt-strong tnt-strong--soft">
          {displayRow.destination}
        </div>
      </td>

      <td className="tnt-td">
        <CodeNamePair name={displayRow.tntVendorName} />
      </td>

      <td className="tnt-td">
        <MaterialCell desc={displayRow.tntMaterialDesc} />
      </td>

      <td className="tnt-td">
        <div className="tnt-strong tnt-strong--soft">
          {displayRow.lastLocation || "—"}
        </div>
      </td>

      <td className="tnt-td">
        <div className="tnt-mono">
          {formatDistanceRemainingKm(displayRow.distanceRemaining)}
        </div>
      </td>

      <td className="tnt-td tnt-td-divider">
        <div className="tnt-mono">
          {formatDisplayDdMmYy(displayRow.dispatchDate)}
        </div>
      </td>

      <td className={agentCellClass}>
        <CodeNamePair
          code={displayRow.siteCode}
          name={displayRow.siteCluster}
        />
      </td>

      <td className={agentCellClass}>
        <CodeNamePair
          code={displayRow.vendorCode}
          name={displayRow.agentVendorName}
        />
      </td>

      <td className={agentMaterialCellClass}>
        <MaterialCell
          code={displayRow.materialCode}
          desc={displayRow.agentMaterialDesc}
        />
      </td>

      <td className="tnt-td tnt-td-center">
        <span className={confidenceClass(displayRow.confidence)}>
          {displayRow.confidence}%
        </span>
      </td>

      <TntActionCell
        rowId={rowId}
        rowState={rowState}
        pendingAction={pendingAction}
        onResetRowAction={onResetRowAction}
        onAction={onAction}
      />

      <td className="tnt-td tnt-td-center tnt-td-divider">
        <div className="tnt-mono">
          {formatDisplayDdMmYy(displayRow.poDispatchDate)}
        </div>
      </td>

      <td className="tnt-td tnt-td-center tnt-td-divider">
        <div className="tnt-po-cell">
          <span className="tnt-code">
            {displayRow.poNumber}
          </span>
        </div>
      </td>
    </tr>
  );
}

TntMainRow.propTypes = {
  rowId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,

  rowState: PropTypes.object,
  showAgentOverride: PropTypes.bool,

  pendingAction: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.object,
  ]),

  onOpenTrip: PropTypes.func.isRequired,
  onResetRowAction: PropTypes.func,
  onAction: PropTypes.func,

  displayRow: PropTypes.shape({
    tripId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    destination: PropTypes.string,
    tntVendorName: PropTypes.string,
    tntMaterialDesc: PropTypes.string,
    lastLocation: PropTypes.string,
    distanceRemaining: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    dispatchDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    siteCode: PropTypes.string,
    siteCluster: PropTypes.string,
    vendorCode: PropTypes.string,
    agentVendorName: PropTypes.string,
    materialCode: PropTypes.string,
    agentMaterialDesc: PropTypes.string,
    confidence: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    poDispatchDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    poNumber: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }).isRequired,
};

function TntDataRow({
  row,
  rowState,
  rowOverride,
  pendingAction,
  isShowingDropdowns,
  dropdownSelections,
  correctionOptions,
  tableData,
  filterOptions,
  onOpenTrip,
  onResetRowAction,
  onAction,
  onDropdownChange,
  onConfirmCorrection,
  onCancelCorrection,
}) {
  const rowId = row.uiRowId;

  const correctionPreview = getCorrectionPreview({
    isShowingDropdowns,
    rowId,
    dropdownSelections,
    correctionOptions,
    tableData,
    filterOptions,
  });

  const displayRow = buildDisplayRow(
    row,
    rowOverride,
    correctionPreview,
  );

  const showAgentOverride = shouldShowAgentOverride(
    rowState,
    row,
    correctionPreview,
  );

  return (
    <Fragment key={rowId}>
      <TntMainRow
        rowId={rowId}
        rowState={rowState}
        displayRow={displayRow}
        showAgentOverride={showAgentOverride}
        pendingAction={pendingAction}
        onOpenTrip={onOpenTrip}
        onResetRowAction={onResetRowAction}
        onAction={onAction}
      />

      {isShowingDropdowns ? (
        <CorrectionRow
          rowId={rowId}
          selections={dropdownSelections}
          onSelect={onDropdownChange}
          onConfirm={onConfirmCorrection}
          onCancel={onCancelCorrection}
        />
      ) : null}
    </Fragment>
  );
}

TntDataRow.propTypes = {
  row: PropTypes.shape({
    uiRowId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
  }).isRequired,

  rowState: PropTypes.object,
  rowOverride: PropTypes.object,
  pendingAction: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.object,
  ]),

  isShowingDropdowns: PropTypes.bool,

  dropdownSelections: PropTypes.object,
  correctionOptions: PropTypes.object,
  tableData: PropTypes.array,
  filterOptions: PropTypes.object,

  onOpenTrip: PropTypes.func,
  onResetRowAction: PropTypes.func,
  onAction: PropTypes.func,
  onDropdownChange: PropTypes.func,
  onConfirmCorrection: PropTypes.func,
  onCancelCorrection: PropTypes.func,
};

const TntLinkagePage = ({ alertType }) => {
  useRegisterAiChatScreen(true);
  const [rowStates, setRowStates] = useState({});
  const [showingDropdowns, setShowingDropdowns] = useState(null);
  const [dropdownSelections, setDropdownSelections] = useState({});
  /**
   * Confirmed corrections per row index. Each entry is a partial row used
   * to override the SAP-side fields when rendering — { siteCode, siteCluster,
   * supplier, vendorName, materialCode, material }.
   */
  const [rowOverrides, setRowOverrides] = useState({});
  /**
   * Unsaved user actions keyed by `uiRowId`. Persists across pagination until
   * saved via `/api/v1/tnt/action`.
   */
  const [pendingActions, setPendingActions] = useState({});
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  /** Multi-select filter values keyed by user-prefs filter names; each entry is string[]. */
  const [selectedFilters, setSelectedFilters] = useState(() =>
    normalizePersistedTntSelectedFilters(
      loadPersistedSelectedFilters(TNT_LINKAGE_SELECTED_FILTERS_STORAGE_KEY, {}),
    ),
  );
  const [isSaving, setIsSaving] = useState(false);
  /** Controls visibility of the Save-Actions confirmation modal. */
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  /** Warn before pagination/filter changes when linkage actions are unsaved. */
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const pendingGuardActionRef = useRef(null);
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [rmpmFilters, setRmpmFilters] = useState([]);
  /** `null` until `/user-prefs` returns; drives toolbar favourites + More Filters order */
  const [userPrefsFilters, setUserPrefsFilters] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [trackingModal, setTrackingModal] = useState(null);

  const trackingTripIds = useMemo(() => {
    const ids = tableData
      .map((row) => row.tripId)
      .filter((id) => id != null && String(id).trim() !== "");
    return [...new Set(ids)];
  }, [tableData]);

  const trackingRowsByTripId = useMemo(() => {
    const map = {};
    tableData.forEach((row) => {
      if (row.tripId) map[row.tripId] = row;
    });
    return map;
  }, [tableData]);

  const hasUnsavedChanges = Object.keys(pendingActions).length > 0;

  const upsertPendingAction = useCallback((rowId, rowState, override = {}) => {
    setPendingActions((prev) => ({
      ...prev,
      [rowId]: { rowState, override },
    }));
  }, []);

  const clearUnsavedLinkageChanges = useCallback((ids) => {
    const rowIds = Array.isArray(ids) ? ids : [];
    setPendingActions({});
    if (rowIds.length === 0) return;
    setRowOverrides((prev) => {
      const next = { ...prev };
      rowIds.forEach((id) => delete next[id]);
      return next;
    });
    setRowStates((prev) => {
      const next = { ...prev };
      rowIds.forEach((id) => delete next[id]);
      return next;
    });
    setShowingDropdowns(null);
  }, []);

  /** Undo a single unsaved row action so the user can pick Accept/Reject again. */
  const handleResetRowAction = useCallback((rowId) => {
    setPendingActions((prev) => {
      if (!prev[rowId]) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setRowStates((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setRowOverrides((prev) => {
      if (!prev[rowId]) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setDropdownSelections((prev) => {
      if (!prev[rowId]) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setShowingDropdowns((prev) => (prev === rowId ? null : prev));
  }, []);

  const guardUnsaved = useCallback((action) => {
    if (Object.keys(pendingActions).length === 0) {
      action();
      return;
    }
    pendingGuardActionRef.current = action;
    setDiscardConfirmOpen(true);
  }, [pendingActions]);

  const handleCancelDiscard = useCallback(() => {
    pendingGuardActionRef.current = null;
    setDiscardConfirmOpen(false);
  }, []);

  const handleConfirmDiscard = useCallback(() => {
    const action = pendingGuardActionRef.current;
    pendingGuardActionRef.current = null;
    setDiscardConfirmOpen(false);
    clearUnsavedLinkageChanges(Object.keys(pendingActions));
    if (typeof action === "function") action();
  }, [pendingActions, clearUnsavedLinkageChanges]);

  const goToPage = useCallback(
    (page) => {
      guardUnsaved(() => setCurrentPage(page));
    },
    [guardUnsaved],
  );

  const handlePageChange = useCallback(
    (page) => {
      const nextPage = Math.max(1, Math.min(page, totalPages || 1));
      goToPage(nextPage);
    },
    [goToPage, totalPages],
  );

  const handleItemsPerPageChange = useCallback(
    (items) => {
      guardUnsaved(() => {
        setItemsPerPage(items);
        setCurrentPage(1);
      });
    },
    [guardUnsaved],
  );

  const fetchUserPrefs = useCallback(async (email) => {
    if (!email) {
      setUserPrefsFilters([]);
      return;
    }
    try {
      const data = await emailTriageGetUserPrefs(email, TNT_USER_PREFS_SCREEN_ID);
      const filtersFromApi = Array.isArray(data?.filters) ? data.filters : [];
      setUserPrefsFilters(filtersFromApi);
    } catch (err) {
      console.error("Failed to fetch TnT user prefs (using defaults):", err);
      setUserPrefsFilters([]);
    }
  }, []);

  const saveUserPrefs = useCallback(
    async (filters) => {
      const email = getUserEmailFromStorage();
      if (!email) return;
      try {
        const payload = buildTntUserPrefsPayload(email, filters);
        await emailTriageSaveUserPrefs(payload);
      } catch (err) {
        console.error("Failed to save TnT user preferences:", err);
      }
    },
    [],
  );

  useEffect(() => {
    const email = getUserEmailFromStorage();
    fetchUserPrefs(email);
  }, [fetchUserPrefs]);

  useEffect(() => {
    savePersistedSelectedFilters(TNT_LINKAGE_SELECTED_FILTERS_STORAGE_KEY, selectedFilters);
  }, [selectedFilters]);

  useEffect(() => {
    if (userPrefsFilters === null) return;

    setFilterOptions((prev) => {
      const next = buildEmptyTntFilterOptionsFromUserPrefs(userPrefsFilters);
      Object.entries(prev).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) next[key] = values;
      });
      return next;
    });

    setSelectedFilters((prev) => mergeTntSelectedFiltersWithUserPrefs(prev, userPrefsFilters));

    setRmpmFilters((prev) =>
      buildRmpmFiltersFromUserPrefs(userPrefsFilters, {
        prev,
        selectedFilters,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrefsFilters]);

  /** Warn when leaving the page with unsaved linkage actions. */
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges]);

  /** Refetch filter option lists when selections change (cascading `/api/v1/tnt/get_filters`). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await tntLinkageGetFilters(selectedFilters);
        if (!cancelled && data) {
          setFilterOptions((prev) => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error("Failed to fetch TnT filters:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFilters]);

  const handleColumnSort = useCallback(
    (column, forcedDirection) => {
      if (!column || column.sortable === false) return;
      const sortKey = column.sortKey ?? column.key;
      guardUnsaved(() => {
        setCurrentPage(1);
        setSortConfig((prev) => {
          const sameColumn = prev.key === sortKey;
          // Re-clicking the applied direction clears sorting.
          if (forcedDirection) {
            return sameColumn && prev.direction === forcedDirection
              ? { key: null, direction: "asc" }
              : { key: sortKey, direction: forcedDirection };
          }
          if (!sameColumn) return { key: sortKey, direction: "asc" };
          return prev.direction === "asc"
            ? { key: sortKey, direction: "desc" }
            : { key: null, direction: "asc" };
        });
      });
    },
    [guardUnsaved],
  );

  /** Fetch paginated linkage rows; filters and sort are applied server-side. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTableLoading(true);
      try {
        const data = await tntLinkageGetTntData({
          page: currentPage,
          pageSize: itemsPerPage,
          filters: selectedFilters,
          sort: sortConfig.key
            ? [{ key: sortConfig.key, direction: sortConfig.direction }]
            : undefined,
        });
        if (cancelled) return;
        setTableData(data.items);
        setTotalItems(data.total);
        setTotalPages(data.totalPages);
        setItemsPerPage(data.pageSize);
        if (data.page !== currentPage) setCurrentPage(data.page);

        setRowStates((prev) => {
          const next = { ...prev };
          for (const row of data.items) {
            const id = row.uiRowId;
            if (next[id]) continue;
            const status = String(row.poLinkageStatus ?? "").trim().toLowerCase();
            if (status === "matched") next[id] = "matched";
            else if (status === "rejected") next[id] = "rejected";
            else if (status === "user_validated" || status === "user-validated") {
              next[id] = "user-validated";
            }
          }
          return next;
        });
      } catch (error) {
        console.error("Failed to fetch TnT linkage data:", error);
        if (!cancelled) {
          setTableData([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentPage, itemsPerPage, selectedFilters, tableRefreshKey, sortConfig]);

  const getFilterOptionsForKey = useCallback(
    (key) => formatTntFilterOptionsForDisplay(key, filterOptions[key] || []),
    [filterOptions],
  );
  const rmpmFiltersOption = useMemo(() => {
    const o = {};
    rmpmFilters.forEach((f) => {
      const key = resolveToolbarFilterKey(f, filterOptions);
      if (!key) return;
      o[f.label] = getFilterOptionsForKey(key);
    });
    return o;
  }, [rmpmFilters, filterOptions, getFilterOptionsForKey]);

  const correctionOptions = useMemo(
    () => buildCorrectionOptionsFromFilters(filterOptions, tableData),
    [filterOptions, tableData],
  );

  const handleAction = (rowId, action) => {
    if (action === "not-matched") {
      setShowingDropdowns(rowId);
      return;
    }
    setRowStates((prev) => ({ ...prev, [rowId]: action }));
    setShowingDropdowns(null);
    upsertPendingAction(rowId, action, rowOverrides[rowId] || {});
  };

  const handleDropdownChange = useCallback(
    async (rowId, field, value) => {
      const { code, name } = normalizeMasterSelection(value);

      if (field === "siteCode") {
        if (!code) {
          setDropdownSelections((prev) => ({
            ...prev,
            [rowId]: { ...prev[rowId], siteCode: "", siteCluster: "" },
          }));
          return;
        }
        const siteCluster =
          name ||
          (await resolveSiteClusterForCodeAsync(
            code,
            selectedFilters,
            tableData,
            filterOptions,
            correctionOptions,
          ));
        setDropdownSelections((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            siteCode: code,
            siteCluster,
          },
        }));
        return;
      }

      if (field === "materialCode") {
        if (!code) {
          setDropdownSelections((prev) => ({
            ...prev,
            [rowId]: { ...prev[rowId], materialCode: "", agentMaterialDesc: "" },
          }));
          return;
        }
        const agentMaterialDesc =
          name ||
          (await resolveMaterialDescForCodeAsync(
            code,
            selectedFilters,
            tableData,
            filterOptions,
            correctionOptions,
          ));
        setDropdownSelections((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            materialCode: code,
            agentMaterialDesc,
          },
        }));
        return;
      }

      if (field === "vendorCode") {
        setDropdownSelections((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            vendorCode: code,
            agentVendorName: name,
          },
        }));
      }
    },
    [
      correctionOptions,
      filterOptions,
      selectedFilters,
      tableData,
    ],
  );

  /**
   * Persist the dropdown picks as a row override and flip the row into the
   * `user-validated` state. The override merges into the row when it renders,
   * so the corrected Site / Vendor / Material codes + names appear in the
   * Agent Data columns directly. The in-flight selections are wiped so a
   * future reject opens with an empty correction row.
   */
  const handleConfirmCorrection = (rowId, remark = "") => {
    const selections = dropdownSelections[rowId];
    const hasSelection = hasCorrectionDropdownSelection(selections);
    const override = buildRowOverrideFromSelections(
      selections,
      correctionOptions,
      tableData,
      filterOptions,
    );
    let nextState = "user-validated";

    if (!hasSelection) {
      const trimmedRemark = String(remark ?? "").trim();
      if (!trimmedRemark) return;
      override.correctionRemark = trimmedRemark;
      nextState = "rejected";
    }

    const mergedOverride = { ...rowOverrides[rowId], ...override };
    setRowOverrides((prev) => ({ ...prev, [rowId]: mergedOverride }));
    setDropdownSelections((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setRowStates((prev) => ({ ...prev, [rowId]: nextState }));
    setShowingDropdowns(null);
    upsertPendingAction(rowId, nextState, mergedOverride);
  };

  const handleCancelCorrection = () => setShowingDropdowns(null);

  /** Clicking "Save Actions" surfaces a confirmation modal first. */
  const handleSaveAll = (isOpen) => {
    if (!hasUnsavedChanges || isSaving) return;
    setConfirmSaveOpen(isOpen);
    if (!isOpen) {
      handleConfirmSave();
      setDiscardConfirmOpen(false);
    }

  };

  const handleConfirmSave = async () => {
    const entries = Object.entries(pendingActions);
    if (entries.length === 0 || isSaving) return;

    const reviewerId = getReviewerIdFromStorage();
    if (!reviewerId) {
      window.alert("Unable to save: reviewer email is not available. Please sign in again.");
      return;
    }

    const items = entries.map(([rowId, { rowState, override }]) =>
      buildTntActionItem(rowId, rowState, override),
    );

    setIsSaving(true);
    setConfirmSaveOpen(false);
    try {
      await tntLinkageSaveActions({
        items,
        reviewerId,
        reviewedTs: new Date().toISOString(),
      });
      const savedIds = entries.map(([rowId]) => rowId);
      setPendingActions({});
      setRowOverrides((prev) => {
        const next = { ...prev };
        savedIds.forEach((id) => delete next[id]);
        return next;
      });
      setTableRefreshKey((k) => k + 1);
    } catch (error) {
      console.error("Failed to save TnT linkage actions:", error);
      window.alert(
        `Failed to save actions. ${error?.message || "Please try again."}`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelSave = () => {
    if (isSaving) return;
    setConfirmSaveOpen(false);
  };

  /** Close on Esc, focus first action when the modal opens. */
  useEffect(() => {
    if (!confirmSaveOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleCancelSave();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmSaveOpen, isSaving]);

  useEffect(() => {
    if (!discardConfirmOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleCancelDiscard();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [discardConfirmOpen, handleCancelDiscard]);

  const pendingActionsCount = useMemo(
    () => Object.keys(pendingActions).length,
    [pendingActions],
  );

  /**
   * Handler signature matches `MultiSelectWithSearchInput`:
   * `(label, values)` where `label` is the human-readable filter label and
   * `values` is the new array of selected option strings.
   */

  const updateSelectedFilters = (
    key,
    nextDisplayVals,
    setSelectedFilters,
  ) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: toApiFilterValues(key, nextDisplayVals),
    }));
  };

  const updateRmpmFilters = (
    label,
    nextDisplayVals,
    filterOptions,
    setSelectedFilters,
  ) => {
    setRmpmFilters((prevFilters) => {
      const matched = prevFilters.find((f) => f.label === label);
      const key = resolveToolbarFilterKey(matched, filterOptions);

      if (key) {
        updateSelectedFilters(
          key,
          nextDisplayVals,
          setSelectedFilters,
        );
      }

      return prevFilters.map((f) =>
        f.label === label
          ? { ...f, selectedData: nextDisplayVals }
          : f,
      );
    });
  };

  const handleMultiSelectChange = useCallback(
    (label, values) => {
      guardUnsaved(() => {
        const nextDisplayVals = Array.isArray(values) ? values : [];

        updateRmpmFilters(
          label,
          nextDisplayVals,
          filterOptions,
          setSelectedFilters,
        );

        setCurrentPage(1);
      });
    },
    [filterOptions, guardUnsaved],
  );

  const updateResetRmpmFilters =
  (cleared, filterOptions) =>
  (prevFilters) =>
    prevFilters.map((f) => {
      const key = resolveToolbarFilterKey(f, filterOptions);

      return {
        ...f,
        selectedData: key ? [...(cleared[key] || [])] : [],
      };
    });

const handleResetFilters = useCallback(() => {
  guardUnsaved(() => {
    const cleared = buildDefaultTntSelectedFilters(userPrefsFilters);

    clearPersistedSelectedFilters(
      TNT_LINKAGE_SELECTED_FILTERS_STORAGE_KEY,
    );

    setSelectedFilters(cleared);

    savePersistedSelectedFilters(
      TNT_LINKAGE_SELECTED_FILTERS_STORAGE_KEY,
      cleared,
    );

    setRmpmFilters(
      updateResetRmpmFilters(cleared, filterOptions),
    );

    setSortConfig({ key: null, direction: "asc" });
    setCurrentPage(1);
  });
}, [guardUnsaved, filterOptions, userPrefsFilters]);

  const handleFiltersSaved = useCallback(
    (updatedFilters) => {
      setRmpmFilters(updatedFilters);
      setSelectedFilters((prev) => {
        const next = { ...prev };
        updatedFilters.forEach((f) => {
          const key = resolveToolbarFilterKey(f, filterOptions);
          if (key) next[key] = toApiFilterValues(key, f.selectedData || []);
        });
        return next;
      });
      setCurrentPage(1);
      saveUserPrefs(updatedFilters);
    },
    [filterOptions, saveUserPrefs],
  );

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalItems);
  const openMoreFiltersDrawer = useCallback(() => {
    setRmpmFilters((prev) =>
      prev.map((f) => {
        const key = resolveToolbarFilterKey(f, filterOptions);
        if (!key) return f;
        return {
          ...f,
          selectedData: toDisplayFilterValues(key, selectedFilters[key] || []),
        };
      }),
    );
    setFilterDrawer(true);
  }, [filterOptions, selectedFilters]);

  const closeMoreFiltersDrawer = useCallback(() => {
    setFilterDrawer(false);
  }, []);
  const openCustomiseColumnsDrawer = useCallback(() => {
  }, []);
  const exportToExcel = useCallback(async () => {
    try {
      const data = await emailTriageExportData(selectedFilters);
      const downloadUrl = typeof data === "string" ? data : data?.url ?? data?.download_url;
      if (!downloadUrl) {
        console.error("Export API did not return a download URL");
        return;
      }
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Export failed:", err);
    }
  }, [selectedFilters]);

  const overflowMenuItems = useMemo(
    () => [
      { label: "More Filters", icon: Filters, onClick: openMoreFiltersDrawer },
      // { label: "Customise", icon: Slider, onClick: openCustomiseColumnsDrawer },
      // { label: "Export", icon: Excel, onClick: exportToExcel },
    ],
    [exportToExcel, openMoreFiltersDrawer, openCustomiseColumnsDrawer],
  );

  const favouriteToolbarFilters = useMemo(
    () =>
      rmpmFilters
        .filter((f) => f.isFavourite)
        .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
        .slice(0, VISIBLE_FILTER_LIMIT),
    [rmpmFilters],
  );

  /** Only skeleton on first prefs load — keep filters mounted during table refetch so multi-select stays open. */
  const showFilterSkeleton = userPrefsFilters === null;

  function renderColumnHeader(col) {
    const sortKey = col.sortKey ?? col.key;
    const sortable = col.sortable !== false;
    const isActiveSort = sortConfig.key === sortKey;

    const thClass = [
      "tnt-th",
      "tnt-th--sub",
      col.center ? "tnt-th--center" : "",
      col.headerClass || "",
      sortable ? "email-triage-th-sortable" : "",
    ]
      .filter(Boolean)
      .join(" ");
    let ariaSort;

    if (!sortable) {
      ariaSort = undefined;
    } else if (!isActiveSort) {
      ariaSort = "none";
    } else if (sortConfig.direction === "asc") {
      ariaSort = "ascending";
    } else {
      ariaSort = "descending";
    }

    return (
      <th
        key={col.key}
        className={thClass || undefined}
        onClick={() => sortable && handleColumnSort(col)}
        scope="col"
        aria-sort={ariaSort}
      >
        <span className="email-triage-th-inner">
          <span>{col.label}</span>

          {sortable ? (
            <ColumnSortIcon
              direction={isActiveSort ? sortConfig.direction : null}
              onSort={(dir) => handleColumnSort(col, dir)}
            />
          ) : null}
        </span>
      </th>
    );
  }

  function renderTableHeader() {
    return (
      <thead>
        <tr className="tnt-thead-main">
          <th className="tnt-th tnt-th--main" colSpan={1}>
            Trip ID
          </th>
          <th className="tnt-th tnt-th--main" colSpan={6}>
            T&T Data
          </th>
          <th className="tnt-th tnt-th--main" colSpan={3}>
            Agent Data
          </th>
          <th className="tnt-th tnt-th--main" colSpan={2}>
            Action
          </th>
          <th
            className="tnt-th tnt-th--main tnt-th--main-last"
            colSpan={2}
          >
            PO Matching
          </th>
        </tr>

        <tr className="tnt-thead-sub">
          {TNT_TABLE_COLUMNS.map(renderColumnHeader)}
        </tr>
      </thead>
    );
  }

  function renderTableRows() {
    let rows;

    if (tableLoading) {
      rows = (
        <TntLinkageTableSkeletonRows
          rows={Math.min(itemsPerPage, 10)}
        />
      );
    } else if (tableData.length === 0) {
      rows = (
        <tr>
          <td className="tnt-empty" colSpan={14}>
            No matching rows for the selected filters.
          </td>
        </tr>
      );
    } else {
      rows = tableData.map((row) => {
        const rowId = row.uiRowId;

        return (
          <TntDataRow
            key={rowId}
            row={row}
            rowState={rowStates[rowId]}
            rowOverride={rowOverrides[rowId]}
            pendingAction={pendingActions[rowId]}
            isShowingDropdowns={showingDropdowns === rowId}
            dropdownSelections={dropdownSelections}
            correctionOptions={correctionOptions}
            tableData={tableData}
            filterOptions={filterOptions}
            onOpenTrip={(tripId, displayRow) =>
              setTrackingModal({
                tripId,
                row: displayRow,
              })
            }
            onResetRowAction={handleResetRowAction}
            onAction={handleAction}
            onDropdownChange={handleDropdownChange}
            onConfirmCorrection={handleConfirmCorrection}
            onCancelCorrection={handleCancelCorrection}
          />
        );
      });
    }

    return <>{rows}</>;
  }

  function renderTntTable() {
    return (
      <div className="tnt-table-wrapper" aria-label="T&T linkage table">
        <table className="tnt-table">
          {renderTableHeader()}

          <tbody>
            {renderTableRows()}
          </tbody>
        </table>
      </div>
    );
  }

  function renderPaginationControls() {
    return (
      <>
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() =>
            handlePageChange(currentPage - 1)
          }
          className="page-nav-btn"
          aria-label="Previous page"
        >
          <ChevronLeft
            size={14}
            className="text-gray-600"
          />
        </button>

        <div className="page-numbers">
          {Array.from(
            { length: totalPages },
            (_, i) => i + 1,
          ).map((page) => {
            const showPage =
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 1 &&
                page <= currentPage + 1);

            const showEllipsis =
              page === currentPage - 2 ||
              page === currentPage + 2;

            if (showPage) {
              return (
                <button
                  key={page}
                  type="button"
                  className={`page-btn ${currentPage === page
                    ? "active"
                    : ""
                    }`}
                  onClick={() =>
                    handlePageChange(page)
                  }
                >
                  {page}
                </button>
              );
            }

            if (showEllipsis) {
              return (
                <span
                  key={page}
                  className="page-ellipsis"
                >
                  ...
                </span>
              );
            }

            return null;
          })}
        </div>

        <button
          type="button"
          disabled={
            totalPages === 0 ||
            currentPage >= totalPages
          }
          onClick={() =>
            handlePageChange(currentPage + 1)
          }
          className="page-nav-btn"
          aria-label="Next page"
        >
          <ChevronRight
            size={14}
            className="text-gray-600"
          />
        </button>
      </>
    );
  }

  function renderPagination() {
    if (tableLoading) {
      return <PaginationSkeleton />;
    }

    return (
      <div className="pagination-wrapper">
        <div className="pagination-left">
          <span className="pagination-text">
            Rows per page:
          </span>

          <div className="pagination-select-wrapper">
            <select
              value={itemsPerPage}
              onChange={(e) =>
                handleItemsPerPageChange(
                  Number(e.target.value),
                )
              }
              className="pagination-select"
            >
              {[5, 10, 20, 50].map((size) => (
                <option
                  key={size}
                  value={size}
                >
                  {size}
                </option>
              ))}
            </select>

            <ChevronDown
              size={10}
              className="pagination-select-icon"
            />
          </div>

          <span className="pagination-text">
            {startItem}-{endItem} of {totalItems}
          </span>
        </div>

        <div className="pagination-right">
          {renderPaginationControls()}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div
        className={alertStyles.filters}
        style={{ width: "100%", display: "flex", alignItems: "end", flexWrap: "wrap" }}
      >
        {showFilterSkeleton ? (
          <FilterBarSkeleton count={VISIBLE_FILTER_LIMIT} showActions={false} inline />
        ) : (
          favouriteToolbarFilters.map((f) => {
            const key = resolveToolbarFilterKey(f, filterOptions);
            if (!key) return null;
            const options = getFilterOptionsForKey(key);
            const displayFilterValues = toDisplayFilterValues(
              key,
              selectedFilters[key] || [],
            );
            return (
              <ListItem key={key} className={alertStyles.listCustomClass}>
                <MultiSelectWithSearchInput
                  label={f.label}
                  options={options}
                  filterValues={displayFilterValues}
                  handleFilter={handleMultiSelectChange}
                  disabled={false}
                  type={alertType}
                />
              </ListItem>
            );
          })
        )}

        {!showFilterSkeleton &&
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              className="reset-filters-button"
              onClick={handleResetFilters}
              title="Reset filters"
              aria-label="Reset filters"
            >
              <img
                src={resetBtn}
                alt=""
                aria-hidden="true"
              />
            </button>
            <OverflowMenu menuItems={overflowMenuItems} />
            <button
              type="button"
              onClick={() => handleSaveAll(true)}
              disabled={!hasUnsavedChanges || isSaving}
              className={`tnt-save-btn${hasUnsavedChanges && !isSaving ? " tnt-save-btn--active" : ""
                }`}
            >
              {isSaving ? (
                <>
                  <span className="tnt-spin-dot" aria-hidden />{' '}
                  Saving...
                </>
              ) : (
                <>
                  <Check size={12} aria-hidden />
                  Save Actions
                </>
              )}
            </button>
          </div>}
      </div>

      <div className="tnt-body">
        {renderTntTable()}
        {renderPagination()}
      </div>

      {discardConfirmOpen ? (
        <div className="tnt-modal-backdrop">
          <dialog
            className="tnt-modal"
            open
            aria-labelledby="tnt-discard-confirm-title"
            aria-describedby="tnt-discard-confirm-body"
            onCancel={handleCancelDiscard}
          >
            <div className="tnt-modal-header">
              <span className="tnt-modal-icon" aria-hidden>
                <AlertTriangle size={20} />
              </span>
              <div className="tnt-modal-titles">
                <h2 id="tnt-discard-confirm-title" className="tnt-modal-title">
                  {UNSAVED_CHANGES_DISCARD_TITLE}
                </h2>
                <p className="tnt-modal-subtitle">
                  Unsaved linkage actions on this screen
                </p>
              </div>
              <button
                type="button"
                className="tnt-modal-close"
                onClick={handleCancelDiscard}
                aria-label="Close discard confirmation"
              >
                <X size={16} />
              </button>
            </div>

            <div id="tnt-discard-confirm-body" className="tnt-modal-body">
              <p>{UNSAVED_CHANGES_DISCARD_MESSAGE}</p>
              <p className="tnt-modal-note">
                <strong>{pendingActionsCount}</strong>{' '}
                {pendingActionsCount === 1 ? 'row has' : 'rows have'} unsaved actions
                that will be cleared if you continue.
              </p>
            </div>

            <div className="tnt-modal-footer">
              <button
                type="button"
                className="tnt-btn tnt-btn-secondary"
                onClick={() => handleSaveAll(false)}
                autoFocus
              >
                Save & Continue
              </button>
              <button
                type="button"
                className="tnt-btn tnt-btn-secondary"
                onClick={handleCancelDiscard}
                autoFocus
              >
                Keep editing
              </button>
              <button
                type="button"
                className="tnt-btn tnt-btn-primary"
                onClick={handleConfirmDiscard}
              >
                Discard changes
              </button>
            </div>
          </dialog>
        </div>
      ) : null}

      {confirmSaveOpen ? (
        <div className="tnt-modal-backdrop">
          <dialog
            className="tnt-modal"
            open
            aria-labelledby="tnt-save-confirm-title"
            aria-describedby="tnt-save-confirm-body"
            onCancel={handleCancelSave}
          >
            <div className="tnt-modal-header">
              <span className="tnt-modal-icon" aria-hidden>
                <AlertTriangle size={20} />
              </span>
              <div className="tnt-modal-titles">
                <h2 id="tnt-save-confirm-title" className="tnt-modal-title">
                  Save linkage actions?
                </h2>
                <p className="tnt-modal-subtitle">
                  Confirm before the changes are committed.
                </p>
              </div>
              <button
                type="button"
                className="tnt-modal-close"
                onClick={handleCancelSave}
                aria-label="Close confirmation dialog"
                disabled={isSaving}
              >
                <X size={16} />
              </button>
            </div>

            <div id="tnt-save-confirm-body" className="tnt-modal-body">
              <p>
                You are about to save{' '}
                <strong>
                  {pendingActionsCount}{' '}
                  {pendingActionsCount === 1 ? 'row' : 'rows'}
                </strong>{' '}
                with confirmed actions. This will mark them as final and update the
                downstream linkage records.
              </p>
              <p className="tnt-modal-note">
                Only rows you changed in this session are included. Actions persist
                across pages until you save.
              </p>
            </div>

            <div className="tnt-modal-footer">
              <button
                type="button"
                className="tnt-btn tnt-btn-secondary"
                onClick={handleCancelSave}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="tnt-btn tnt-btn-primary"
                onClick={handleConfirmSave}
                disabled={isSaving}
                autoFocus
              >
                {isSaving ? (
                  <>
                    <span className="tnt-spin-dot" aria-hidden />{' '}
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={12} aria-hidden />{' '}
                    Yes, save changes
                  </>
                )}
              </button>
            </div>
          </dialog>
        </div>
      ) : null}

      <TripTrackingModal
        open={Boolean(trackingModal)}
        tripId={trackingModal?.tripId}
        row={trackingModal?.row}
        tripIds={trackingTripIds}
        rowsByTripId={trackingRowsByTripId}
        alertType={alertType}
        onClose={() => setTrackingModal(null)}
      />

      {filterDrawer && rmpmFilters.length > 0 && (
        <MoreFilters
          anchor="right"
          filterDrawer={filterDrawer}
          handleFilterDrawer={closeMoreFiltersDrawer}
          rmpmFilters={rmpmFilters}
          setRmpmFilters={setRmpmFilters}
          options={rmpmFiltersOption}
          onFiltersSaved={handleFiltersSaved}
          limit={VISIBLE_FILTER_LIMIT}
        />
      )}
    </div>
  );
};

TntLinkagePage.propTypes = {
  alertType: PropTypes.string.isRequired,
};

export default TntLinkagePage;
